import React, { useState, useRef, useEffect } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import {
    FaBrain, FaDollarSign, FaChartLine, FaChartBar, FaBullseye,
    FaShoppingCart, FaCog, FaChartPie, FaUsers, FaSearch, FaMap,
    FaBolt, FaDownload, FaTimes, FaChevronDown, FaChevronUp,
    FaCheck, FaSpinner, FaPlay, FaExpand, FaLightbulb
} from 'react-icons/fa';
import { useLanguage } from '../../i18n';
import DataTableExport from '../chatbotui/DataTableExport';
import { executeSQLDirect } from '../../utils/strandsClient';
import './guia.css';

const STRANDS_API_URL = process.env.REACT_APP_STRANDS_API_URL || 'http://localhost:5001';

// Iconos por rol (usando IDs)
const roleIconsById = {
    "ceo": FaBrain,
    "cfo": FaDollarSign,
    "commercial": FaChartLine,
    "revenue": FaChartBar,
    "marketing": FaBullseye,
    "ecommerce": FaShoppingCart,
    "priceAnalyst": FaCog,
    "categories": FaUsers
};

// Iconos por fase (usando IDs)
const phaseIconsById = {
    "eda": FaSearch,
    "pricing": FaChartBar,
    "elasticity": FaBrain,
    "geographic": FaMap,
    "optimization": FaBolt,
    "export": FaDownload
};

// Prioridades y tiempos por rol (estos son constantes)
const roleMetadata = {
    ceo: { priority: "high", timeEstimate: "15-20 min" },
    cfo: { priority: "high", timeEstimate: "20-25 min" },
    commercial: { priority: "high", timeEstimate: "25-30 min" },
    revenue: { priority: "high", timeEstimate: "25-30 min" },
    marketing: { priority: "medium", timeEstimate: "20-25 min" },
    ecommerce: { priority: "medium", timeEstimate: "15-20 min" },
    priceAnalyst: { priority: "medium", timeEstimate: "30-40 min" },
    categories: { priority: "medium", timeEstimate: "25-30 min" }
};

// Prioridades y tiempos por fase
const phaseMetadata = {
    eda: { priority: "high", timeEstimate: "20-30 min" },
    pricing: { priority: "high", timeEstimate: "25-35 min" },
    elasticity: { priority: "high", timeEstimate: "30-40 min" },
    geographic: { priority: "medium", timeEstimate: "25-30 min" },
    optimization: { priority: "high", timeEstimate: "30-40 min" },
    export: { priority: "medium", timeEstimate: "10-15 min" }
};

// Función para generar rolesData desde traducciones
const getRolesData = (t) => {
    const rolesTranslations = t('guideQueries.roles');
    const roleIds = ['ceo', 'cfo', 'commercial', 'revenue', 'marketing', 'ecommerce', 'priceAnalyst', 'categories'];

    return roleIds.map(id => ({
        id,
        title: rolesTranslations[id]?.title || id.toUpperCase(),
        description: rolesTranslations[id]?.description || '',
        priority: t(`guide.${roleMetadata[id]?.priority || 'medium'}`),
        timeEstimate: roleMetadata[id]?.timeEstimate || '15-20 min',
        consultas: rolesTranslations[id]?.queries || []
    }));
};

// Función para generar phasesData desde traducciones
const getPhasesData = (t) => {
    const phasesTranslations = t('guideQueries.phases');
    const phaseIds = ['eda', 'pricing', 'elasticity', 'geographic', 'optimization', 'export'];

    return phaseIds.map(id => ({
        id,
        title: phasesTranslations[id]?.title || id.toUpperCase(),
        description: phasesTranslations[id]?.description || '',
        priority: t(`guide.${phaseMetadata[id]?.priority || 'medium'}`),
        timeEstimate: phaseMetadata[id]?.timeEstimate || '15-20 min',
        consultas: phasesTranslations[id]?.queries || []
    }));
};

const GuiaInteractiva = () => {
    const { user } = useAuthenticator((context) => [context.user]);
    const { t, language } = useLanguage();
    const username = user?.username || 'anonymous';

    // Helper para extraer texto del idioma correcto (soporta formato string y objeto {es, en})
    const getLocalizedText = (value, fallbackLang = 'es') => {
        if (typeof value === 'string') return value;
        if (value && typeof value === 'object') {
            return value[language] || value[fallbackLang] || value.es || value.en || '';
        }
        return '';
    };

    // Helper para extraer preguntas del formato multi-idioma
    const getLocalizedQuestions = (questions) => {
        if (!Array.isArray(questions)) return [];
        return questions.map(q => {
            if (typeof q === 'string') return { text: q };
            if (q && typeof q === 'object') {
                const text = q[language] || q.es || q.en || q.text || '';
                return { text };
            }
            return { text: String(q) };
        });
    };

    // Estado para datos cargados desde API
    const [rolesData, setRolesData] = useState([]);
    const [phasesData, setPhasesData] = useState([]);
    const [isLoadingGuide, setIsLoadingGuide] = useState(true);

    // Estados principales
    const [activeTab, setActiveTab] = useState('roles'); // 'roles' o 'fases'
    const [expandedSections, setExpandedSections] = useState({});
    const [completedQueries, setCompletedQueries] = useState({});

    // Estados del modal
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedQuery, setSelectedQuery] = useState(null);
    const [loading, setLoading] = useState(false);
    const [agentResponse, setAgentResponse] = useState('');
    const [thinkingMessage, setThinkingMessage] = useState('');
    const [showThinking, setShowThinking] = useState(false);

    // Estados de visualización
    const [vizLoading, setVizLoading] = useState(false);
    const [vizData, setVizData] = useState(null);
    const [vizError, setVizError] = useState(null);
    const [capturedSQL, setCapturedSQL] = useState(null);

    // Cache de resultados
    const [queryCache, setQueryCache] = useState({});

    // Refs
    const abortControllerRef = useRef(null);
    const responseContainerRef = useRef(null);

    // Cargar guía interactiva desde API
    useEffect(() => {
        const fetchInteractiveGuide = async () => {
            if (!user) {
                // Sin usuario, usar datos de traducciones como fallback
                setRolesData(getRolesData(t));
                setPhasesData(getPhasesData(t));
                setIsLoadingGuide(false);
                return;
            }

            try {
                setIsLoadingGuide(true);
                const response = await fetch(
                    `${STRANDS_API_URL}/api/user/interactive-guide?username=${encodeURIComponent(username)}`
                );

                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.interactive_guide) {
                        const guide = data.interactive_guide;

                        // Transformar guide_by_role al formato del componente (con soporte multi-idioma)
                        if (guide.guide_by_role && Object.keys(guide.guide_by_role).length > 0) {
                            const transformedRoles = Object.entries(guide.guide_by_role).map(([roleId, roleData]) => ({
                                id: roleId,
                                title: getLocalizedText(roleData.title) || roleId,
                                description: getLocalizedText(roleData.description) || '',
                                icon: roleData.icon || null, // Icono emoji personalizado
                                priority: roleData.priority || 'medium',
                                timeEstimate: roleData.timeEstimate || '15-20 min',
                                consultas: getLocalizedQuestions(roleData.questions || [])
                            }));
                            setRolesData(transformedRoles);
                            console.log('[GUIA] Roles cargados desde API:', transformedRoles.length, 'idioma:', language);
                        } else {
                            // Fallback a traducciones
                            setRolesData(getRolesData(t));
                        }

                        // Transformar guide_by_phase al formato del componente (con soporte multi-idioma)
                        if (guide.guide_by_phase && Object.keys(guide.guide_by_phase).length > 0) {
                            const transformedPhases = Object.entries(guide.guide_by_phase).map(([phaseId, phaseData]) => ({
                                id: phaseId,
                                title: getLocalizedText(phaseData.title) || phaseId,
                                description: getLocalizedText(phaseData.description) || '',
                                icon: phaseData.icon || null, // Icono emoji personalizado
                                priority: phaseData.priority || 'medium',
                                timeEstimate: phaseData.timeEstimate || '15-20 min',
                                consultas: getLocalizedQuestions(phaseData.questions || [])
                            }));
                            setPhasesData(transformedPhases);
                            console.log('[GUIA] Fases cargadas desde API:', transformedPhases.length, 'idioma:', language);
                        } else {
                            // Fallback a traducciones
                            setPhasesData(getPhasesData(t));
                        }
                    } else {
                        // Sin datos de API, usar traducciones
                        setRolesData(getRolesData(t));
                        setPhasesData(getPhasesData(t));
                    }
                } else {
                    // Error de API, usar traducciones
                    setRolesData(getRolesData(t));
                    setPhasesData(getPhasesData(t));
                }
            } catch (error) {
                console.error('[GUIA] Error loading interactive guide:', error);
                // Fallback a traducciones
                setRolesData(getRolesData(t));
                setPhasesData(getPhasesData(t));
            } finally {
                setIsLoadingGuide(false);
            }
        };

        fetchInteractiveGuide();
    }, [user, username, t, language]);

    // Calcular progreso
    const calculateProgress = () => {
        const data = activeTab === 'roles' ? rolesData : phasesData;
        const totalQueries = data.reduce((acc, section) => acc + section.consultas.length, 0);
        const completed = Object.keys(completedQueries).filter(key =>
            key.startsWith(activeTab === 'roles' ? 'role_' : 'phase_')
        ).length;
        return {
            completed,
            total: totalQueries,
            percentage: totalQueries > 0 ? Math.round((completed / totalQueries) * 100) : 0
        };
    };

    // Toggle sección expandida
    const toggleSection = (sectionId) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };

    // Generar cache key
    const getCacheKey = (query) => {
        return query.text.toLowerCase().trim().replace(/\s+/g, '_').substring(0, 50);
    };

    // Manejar click en consulta
    const handleQueryClick = async (query, sectionId, queryIndex) => {
        const queryKey = `${activeTab === 'roles' ? 'role' : 'phase'}_${sectionId}_${queryIndex}`;
        const cacheKey = getCacheKey(query);

        setSelectedQuery({ ...query, sectionId, queryIndex, queryKey });
        setModalOpen(true);
        setAgentResponse('');
        setVizData(null);
        setVizError(null);
        setCapturedSQL(null);

        // Verificar cache
        if (queryCache[cacheKey]) {
            console.log('[GUIA] Usando respuesta cacheada');
            setAgentResponse(queryCache[cacheKey].response);
            setCapturedSQL(queryCache[cacheKey].sql);
            if (queryCache[cacheKey].vizData) {
                setVizData(queryCache[cacheKey].vizData);
            }
            // Marcar como completada
            setCompletedQueries(prev => ({ ...prev, [queryKey]: true }));
            return;
        }

        // Llamar al agente
        await invokeAgent(query.text, cacheKey, queryKey);
    };

    // Invocar agente con streaming
    const invokeAgent = async (queryText, cacheKey, queryKey) => {
        setLoading(true);
        setShowThinking(true);
        setThinkingMessage(t('guide.processingQuery'));

        abortControllerRef.current = new AbortController();

        try {
            const response = await fetch(`${STRANDS_API_URL}/query-stream`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'text/event-stream'
                },
                body: JSON.stringify({
                    query: queryText,
                    username: username,
                    conversation_history: [],
                    language: language // Enviar idioma para que el agente responda en el idioma correcto
                }),
                signal: abortControllerRef.current.signal
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let accumulatedResponse = '';
            let capturedSql = null;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const event = JSON.parse(line.slice(6));

                            switch (event.type) {
                                case 'thinking':
                                case 'reasoning':
                                case 'thinking_stream':
                                    setThinkingMessage(event.content || 'Procesando...');
                                    break;

                                case 'tool_use':
                                    setThinkingMessage(`Usando herramienta: ${event.tool}`);
                                    break;

                                case 'final_response_start':
                                    setShowThinking(false);
                                    setThinkingMessage('');
                                    break;

                                case 'chunk':
                                    if (event.content) {
                                        setShowThinking(false);
                                        accumulatedResponse += event.content;
                                        setAgentResponse(accumulatedResponse);
                                    }
                                    break;

                                case 'done':
                                    capturedSql = event.captured_sql || null;
                                    setCapturedSQL(capturedSql);
                                    setShowThinking(false);
                                    setLoading(false);

                                    // Guardar en cache
                                    setQueryCache(prev => ({
                                        ...prev,
                                        [cacheKey]: {
                                            response: accumulatedResponse,
                                            sql: capturedSql,
                                            timestamp: Date.now()
                                        }
                                    }));

                                    // Marcar como completada
                                    setCompletedQueries(prev => ({ ...prev, [queryKey]: true }));
                                    break;

                                case 'error':
                                    throw new Error(event.error || 'Error del servidor');

                                default:
                                    break;
                            }
                        } catch (parseError) {
                            console.warn('[GUIA] Error parseando evento:', parseError);
                        }
                    }
                }
            }

            // Si no se recibió evento 'done'
            if (accumulatedResponse && !capturedSql) {
                setQueryCache(prev => ({
                    ...prev,
                    [cacheKey]: {
                        response: accumulatedResponse,
                        sql: null,
                        timestamp: Date.now()
                    }
                }));
                setCompletedQueries(prev => ({ ...prev, [queryKey]: true }));
            }

        } catch (error) {
            if (error.name === 'AbortError') {
                console.log('[GUIA] Request cancelado');
            } else {
                console.error('[GUIA] Error:', error);
                setAgentResponse(`Error: ${error.message}`);
            }
        } finally {
            setLoading(false);
            setShowThinking(false);
        }
    };

    // Cargar visualización
    const handleLoadVisualization = async () => {
        if (!capturedSQL) {
            setVizError('No hay SQL disponible para visualizar');
            return;
        }

        setVizLoading(true);
        setVizError(null);

        try {
            console.log('[GUIA] Ejecutando SQL:', capturedSQL.substring(0, 100));
            const result = await executeSQLDirect(capturedSQL, null, username);

            if (result.success && result.data) {
                const vizResult = {
                    data: result.data,
                    columns: result.metadata?.columns || Object.keys(result.data[0] || {}),
                    column_types: result.metadata?.column_types || {},
                    query: capturedSQL
                };

                setVizData(vizResult);

                // Actualizar cache con vizData
                if (selectedQuery) {
                    const cacheKey = getCacheKey(selectedQuery);
                    setQueryCache(prev => ({
                        ...prev,
                        [cacheKey]: {
                            ...prev[cacheKey],
                            vizData: vizResult
                        }
                    }));
                }
            } else {
                throw new Error(result.error || 'Error ejecutando SQL');
            }
        } catch (error) {
            console.error('[GUIA] Error cargando visualización:', error);
            setVizError(error.message);
        } finally {
            setVizLoading(false);
        }
    };

    // Cerrar modal
    const handleCloseModal = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        setModalOpen(false);
        setSelectedQuery(null);
        setAgentResponse('');
        setVizData(null);
        setVizError(null);
        setLoading(false);
        setShowThinking(false);
    };

    // Renderizar sección
    const renderSection = (section, isRole = true) => {
        // Soporte para iconos personalizados (emojis) o iconos de React por defecto
        const hasCustomIcon = section.icon && typeof section.icon === 'string';
        const Icon = isRole ? roleIconsById[section.id] || FaUsers : phaseIconsById[section.id] || FaChartBar;
        const isExpanded = expandedSections[section.id];
        const completedCount = section.consultas.filter((_, idx) =>
            completedQueries[`${isRole ? 'role' : 'phase'}_${section.id}_${idx}`]
        ).length;

        return (
            <div key={section.id} className={`guia-section ${isExpanded ? 'expanded' : ''}`}>
                <div className="section-header" onClick={() => toggleSection(section.id)}>
                    <div className="section-icon-wrapper">
                        {hasCustomIcon ? (
                            <span className="section-emoji-icon">{section.icon}</span>
                        ) : (
                            <Icon className="section-icon" />
                        )}
                    </div>
                    <div className="section-info">
                        <h3 className="section-title">{section.title}</h3>
                        <p className="section-description">{section.description}</p>
                        <div className="section-meta">
                            <span className={`priority-badge ${section.priority.toLowerCase()}`}>
                                {section.priority}
                            </span>
                            <span className="time-badge">
                                {section.timeEstimate}
                            </span>
                            <span className="progress-badge">
                                {completedCount}/{section.consultas.length} {t('guide.queriesCount')}
                            </span>
                        </div>
                    </div>
                    <div className="section-toggle">
                        {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                    </div>
                </div>

                {isExpanded && (
                    <div className="section-queries">
                        {section.consultas.map((query, idx) => {
                            const queryKey = `${isRole ? 'role' : 'phase'}_${section.id}_${idx}`;
                            const isCompleted = completedQueries[queryKey];

                            return (
                                <div
                                    key={idx}
                                    className={`query-item ${isCompleted ? 'completed' : ''}`}
                                    onClick={() => handleQueryClick(query, section.id, idx)}
                                >
                                    <span className="query-number">{idx + 1}</span>
                                    <span className="query-text">{query.text}</span>
                                    <span className="query-action">
                                        {isCompleted ? (
                                            <FaCheck className="check-icon" />
                                        ) : (
                                            <FaPlay className="play-icon" />
                                        )}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    const progress = calculateProgress();

    return (
        <div className="guia-container">
            {/* Header */}
            <div className="guia-header">
                <div className="header-content">
                    <div className="header-icon">
                        <FaLightbulb />
                    </div>
                    <div className="header-text">
                        <h1>{t('guide.title')}</h1>
                        <p>{t('guide.subtitle')}</p>
                    </div>
                </div>

                {/* Tabs */}
                <div className="guia-tabs">
                    <button
                        className={`tab-btn ${activeTab === 'roles' ? 'active' : ''}`}
                        onClick={() => setActiveTab('roles')}
                    >
                        <FaUsers />
                        <span>{t('guide.byRole')}</span>
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'fases' ? 'active' : ''}`}
                        onClick={() => setActiveTab('fases')}
                    >
                        <FaChartLine />
                        <span>{t('guide.byPhase')}</span>
                    </button>
                </div>
            </div>

            {/* Progress Bar */}
            <div className="guia-progress">
                <div className="progress-info">
                    <span className="progress-text">
                        <strong>{progress.completed}</strong> / <strong>{progress.total}</strong> {t('guide.completed')}
                    </span>
                    <span className="progress-percentage">{progress.percentage}%</span>
                </div>
                <div className="progress-bar-container">
                    <div
                        className="progress-bar-fill"
                        style={{ width: `${progress.percentage}%` }}
                    />
                </div>
            </div>

            {/* Secciones */}
            <div className="guia-sections">
                {isLoadingGuide ? (
                    <div className="loading-container">
                        <FaSpinner className="spinner large" />
                        <p>{t('common.loading') || 'Cargando...'}</p>
                    </div>
                ) : activeTab === 'roles' ? (
                    rolesData.length > 0 ? (
                        rolesData.map(role => renderSection(role, true))
                    ) : (
                        <div className="empty-state">
                            <p>{t('guide.noRolesConfigured') || 'No hay roles configurados para esta guía.'}</p>
                        </div>
                    )
                ) : (
                    phasesData.length > 0 ? (
                        phasesData.map(phase => renderSection(phase, false))
                    ) : (
                        <div className="empty-state">
                            <p>{t('guide.noPhasesConfigured') || 'No hay fases configuradas para esta guía.'}</p>
                        </div>
                    )
                )}
            </div>

            {/* Modal de Consulta */}
            {modalOpen && (
                <div className="guia-modal-overlay" onClick={handleCloseModal}>
                    <div className="guia-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{selectedQuery?.text}</h2>
                            <button className="close-btn" onClick={handleCloseModal}>
                                <FaTimes />
                            </button>
                        </div>

                        <div className="modal-body" ref={responseContainerRef}>
                            {/* Thinking State */}
                            {showThinking && (
                                <div className="thinking-container">
                                    <FaSpinner className="spinner" />
                                    <span className="thinking-text">{thinkingMessage}</span>
                                </div>
                            )}

                            {/* Response */}
                            {agentResponse && (
                                <div className="response-container">
                                    <ReactMarkdown
                                        remarkPlugins={[remarkGfm]}
                                        components={{
                                            code({ node, inline, className, children, ...props }) {
                                                const match = /language-(\w+)/.exec(className || '');
                                                return !inline && match ? (
                                                    <SyntaxHighlighter
                                                        style={tomorrow}
                                                        language={match[1]}
                                                        PreTag="div"
                                                        {...props}
                                                    >
                                                        {String(children).replace(/\n$/, '')}
                                                    </SyntaxHighlighter>
                                                ) : (
                                                    <code className={className} {...props}>
                                                        {children}
                                                    </code>
                                                );
                                            },
                                            table({ children }) {
                                                return (
                                                    <div className="table-wrapper">
                                                        <table className="markdown-table">{children}</table>
                                                    </div>
                                                );
                                            }
                                        }}
                                    >
                                        {agentResponse}
                                    </ReactMarkdown>
                                </div>
                            )}

                            {/* Visualization Controls */}
                            {capturedSQL && !vizData && (
                                <div className="viz-controls">
                                    <button
                                        className="viz-btn"
                                        onClick={handleLoadVisualization}
                                        disabled={vizLoading}
                                    >
                                        {vizLoading ? (
                                            <>
                                                <FaSpinner className="spinner" />
                                                {t('guide.loadingData')}
                                            </>
                                        ) : (
                                            <>
                                                <FaExpand />
                                                {t('guide.viewFullData')}
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}

                            {/* Viz Error */}
                            {vizError && (
                                <div className="viz-error">
                                    <p>Error: {vizError}</p>
                                </div>
                            )}

                            {/* Visualization */}
                            {vizData && (
                                <div className="visualization-container">
                                    <DataTableExport
                                        data={vizData.data}
                                        metadata={{
                                            columns: vizData.columns,
                                            column_types: vizData.column_types
                                        }}
                                        sql={vizData.query}
                                    />
                                </div>
                            )}

                            {/* Loading State */}
                            {loading && !showThinking && !agentResponse && (
                                <div className="loading-container">
                                    <FaSpinner className="spinner large" />
                                    <p>{t('guide.processingQuery')}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GuiaInteractiva;
